import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { AttendanceRecord } from '@/api/learnerProfile'
import { cn } from '@/lib/utils'

const CALENDAR_CELL_COUNT = 42

function parseDateKey(dateKey: string) {
  const [year, month, day] = dateKey.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1)
}

function dateKey(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

interface AttendanceCalendarProps {
  items: AttendanceRecord[]
}

export function AttendanceCalendar({ items }: AttendanceCalendarProps) {
  const { t, i18n } = useTranslation('learner')
  const [monthOffset, setMonthOffset] = useState(0)
  const baseMonth = useMemo(
    () => (items[0]?.date ? parseDateKey(items[0].date) : new Date()),
    [items],
  )
  const visibleMonth = addMonths(baseMonth, monthOffset)
  const year = visibleMonth.getFullYear()
  const month = visibleMonth.getMonth()
  const firstWeekday = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const attendedDates = useMemo(
    () => new Set(items.filter((item) => item.status === 'PRESENT').map((item) => item.date)),
    [items],
  )
  const monthLabel = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'ko', {
    year: 'numeric',
    month: 'long',
  }).format(visibleMonth)
  const weekdays = t('profile.attendance.calendar.weekdays', {
    returnObjects: true,
  }) as string[]

  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl bg-lc-v3-gray-50 p-4 lg:w-[360px] lg:shrink-0">
      <div className="flex items-center justify-between px-2">
        <strong className="text-lc-v3-body-1 font-bold text-lc-v3-gray-900">
          {monthLabel}
        </strong>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setMonthOffset((offset) => offset - 1)}
            aria-label={t('profile.attendance.calendar.previousMonth')}
            className="flex size-8 items-center justify-center rounded-full border border-lc-disabled-border bg-lc-bg-primary text-lc-text-secondary hover:bg-lc-v3-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setMonthOffset((offset) => offset + 1)}
            aria-label={t('profile.attendance.calendar.nextMonth')}
            className="flex size-8 items-center justify-center rounded-full border border-lc-disabled-border bg-lc-bg-primary text-lc-text-secondary hover:bg-lc-v3-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2" role="grid" aria-label={monthLabel}>
        {weekdays.map((weekday, index) => (
          <span
            key={weekday}
            role="columnheader"
            className={cn(
              'text-center text-lc-v3-caption-1 font-semibold',
              index === 0
                ? 'text-lc-error-600'
                : index === 6
                  ? 'text-lc-key-600'
                  : 'text-lc-text-secondary',
            )}
          >
            {weekday}
          </span>
        ))}
        {Array.from({ length: CALENDAR_CELL_COUNT }, (_, index) => {
          const day = index - firstWeekday + 1
          if (day < 1 || day > daysInMonth) {
            return <span key={`empty-${index}`} className="h-9" aria-hidden="true" />
          }

          const weekday = index % 7
          const key = dateKey(year, month, day)
          const attended = attendedDates.has(key)
          return (
            <span
              key={key}
              role="gridcell"
              aria-label={
                attended
                  ? t('profile.attendance.calendar.attendedDate', { date: key })
                  : key
              }
              className={cn(
                'flex size-9 items-center justify-center justify-self-center rounded-full text-sm font-semibold',
                attended
                  ? 'bg-lc-key-500 text-lc-text-on-key'
                  : weekday === 0
                    ? 'text-lc-error-600'
                    : weekday === 6
                      ? 'text-lc-key-600'
                      : 'text-lc-v3-gray-900',
              )}
            >
              {day}
            </span>
          )
        })}
      </div>

      <div className="flex items-center justify-end gap-2 text-lc-v3-caption-1 font-semibold text-lc-text-link">
        <span className="size-2 rounded-full bg-lc-key-500" aria-hidden="true" />
        {t('profile.attendance.calendar.legend')}
      </div>
    </div>
  )
}
